import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const searchSchema = z.string().trim().max(100, 'Search query too long');

interface SearchResult {
  label: string;
  entity: string;
  path: string;
}

export function SearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    const fetchData = async () => {
      if (query.length < 2) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      // Validate input
      const validation = searchSchema.safeParse(query);
      if (!validation.success) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const { data, error } = await supabase.rpc('search_debtnet', { q: query });
        
        if (error) throw error;
        
        setResults(data || []);
        setIsOpen(true);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchData, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleResultClick = (path: string) => {
    navigate(path);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setIsMobileOpen(false);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const toggleMobileSearch = () => {
    setIsMobileOpen(!isMobileOpen);
    if (!isMobileOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  return (
    <>
      {/* Desktop Search */}
      <div className="hidden md:block relative w-full max-w-md" ref={dropdownRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search DebtNet..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 pr-9 bg-background/50"
          />
          {query && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={handleClear}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {isOpen && results.length > 0 && (
          <div className="absolute mt-2 w-full bg-card border border-border rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
            {results.map((result, idx) => (
              <div
                key={idx}
                onClick={() => handleResultClick(result.path)}
                className="px-4 py-3 cursor-pointer hover:bg-accent transition-colors flex justify-between items-center group"
              >
                <span className="text-sm font-medium group-hover:text-primary transition-colors">
                  {result.label}
                </span>
                <span className="text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted">
                  {result.entity}
                </span>
              </div>
            ))}
          </div>
        )}

        {isOpen && query.length >= 2 && results.length === 0 && !isLoading && (
          <div className="absolute mt-2 w-full bg-card border border-border rounded-md shadow-lg z-50 px-4 py-3">
            <p className="text-sm text-muted-foreground">No results found</p>
          </div>
        )}
      </div>

      {/* Mobile Search Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={toggleMobileSearch}
      >
        <Search className="h-4 w-4" />
      </Button>

      {/* Mobile Search Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 md:hidden">
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Search DebtNet..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {query && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={handleClear}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={toggleMobileSearch}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-1">
              {results.map((result, idx) => (
                <div
                  key={idx}
                  onClick={() => handleResultClick(result.path)}
                  className="px-4 py-3 cursor-pointer hover:bg-accent rounded-md transition-colors flex justify-between items-center"
                >
                  <span className="text-sm font-medium">{result.label}</span>
                  <span className="text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted">
                    {result.entity}
                  </span>
                </div>
              ))}

              {query.length >= 2 && results.length === 0 && !isLoading && (
                <div className="px-4 py-3">
                  <p className="text-sm text-muted-foreground">No results found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
